import {
    EnvironmentDetail,
    globalEventBus,
    UnsupportedEnvironmentException, InvalidInputException, WakeWordTimeoutException,
    WakeWordListeningInterruptedException, WakeWordInvalidActionException,
    PromiseUtils,
} from '@amzn/ihm-associate-tools-js-lib'
import { SeawardEventBusTopicsConstants } from '@utils/Constants/SeawardEventBusTopicsConstants'
import * as VoiceUtilsService from '@utils/VoiceUtilsService'
import _ from 'lodash'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'

const WakeWordConstants = Object.freeze({
    DEFAULT_TIMEOUT: 600000, // 10 minutes in ms
    MAX_TIMEOUT: 1800000, // 30 minutes in ms
})

export class WakeButtonService {
    public static wakeButtonWarmedUp = false
    public static deferred = null
    public static listenPromiseWithTimeout = null

    /**
     * Function for initialization
     *
     * @return {Promise} Resolves if the initialization of WakeWord was successful
     */
    public static warmup () {

        if (EnvironmentDetail.isAndroid()) {
            console.error('WakeButtonService is not supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'WakeButtonService is not supported on Android.',
            }))
        }

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(() => {
                WakeButtonService.wakeButtonWarmedUp = true
            })
    }

    /**
     * Shows the wake button and waits for someone to click it. Clicking the button mimics the scenario
     * when an associate says the wake word in CandyCane.
     *
     * Listening to wake button will time out after a given timeout in config.
     * Default timeout is 10 minutes, and the maximum is 30 minutes.
     *
     * config parameter includes following items,
     * timeout: wake word listening timeout in ms.
     *
     * @return {promise} Resolves if the wake button was clicked.
     */
    public static listen (config) {

        if (EnvironmentDetail.isAndroid()) {
            console.error('WakeButtonService is not supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'WakeButtonService is not supported on Android.',
            }))
        }

        config = config || {}
        config.timeout = config.timeout || WakeWordConstants.DEFAULT_TIMEOUT

        if (config.timeout > WakeWordConstants.MAX_TIMEOUT) {
            console.warn('Given timeout value ', config.timeout.toString(), 'ms is greater than the maximum timeout')
            return Promise.reject(new InvalidInputException({
                message: 'Given timeout value is greater than maximum value',
            }))
        }
        if (WakeButtonService.deferred) {
            return Promise.reject(new WakeWordInvalidActionException({
                message: 'Invalid Action',
            }))
        }

        WakeButtonService.deferred = generateDeferredPromise<any>()
        const localDeferred = WakeButtonService.deferred
        const listenPromise = VoiceUtilsService.verifyVoiceIsEnabled()
                                .then(() => {
                                    if (localDeferred !== WakeButtonService.deferred) {
                                        // we already timed out and rejected, so we simply return
                                        return
                                    }
                                    WakeButtonService.showButton()
                                    return localDeferred.promise
                                })

        WakeButtonService.listenPromiseWithTimeout = PromiseUtils.executeWithTimeout(listenPromise, {
            durationMillis: config.timeout,
            error: new WakeWordTimeoutException({
                message: 'Listening to wake word timed out',
            }),
        })
            .finally(() => {
                // Clean up
                WakeButtonService.deferred = null
                WakeButtonService.listenPromiseWithTimeout = null
                WakeButtonService.hideButton()
            })

        return WakeButtonService.listenPromiseWithTimeout
    }

    /**
     * Cancels the wake button listening.
     * Rejects deferred promise if any and hides the button.
     *
     * @return {Promise} Resolves if the stop was successful and fails otherwise
     */
    public static cancel () {

        if (EnvironmentDetail.isAndroid()) {
            console.error('WakeButtonService is not supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'WakeButtonService is not supported on Android.',
            }))
        }

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(() => {
                const localListenPromiseWithTimeout = WakeButtonService.listenPromiseWithTimeout
                WakeButtonService.rejectDeferredPromise()
                if (!localListenPromiseWithTimeout) {
                    return
                }
                /**
                 * We do this to avoid a race condition when listen is called right after cancel.
                 * We want to reject the promise and clean up the resources before a listen call comes in.
                 * Since we are rejecting the promise we need to swallow the error.
                 */
                return localListenPromiseWithTimeout.catch(_.noop)
            })
    }

    /**
     * Subscribes to seaward topics
     */
    public static activate () {
        globalEventBus.on(SeawardEventBusTopicsConstants.WAKE_BUTTON_CLICKED, 'wakeButton', WakeButtonService.wakeButtonClicked)
        globalEventBus.on(SeawardEventBusTopicsConstants.LOGOUT, 'wakeWord', WakeButtonService.cancelIfWarmedUp)
        globalEventBus.on(SeawardEventBusTopicsConstants.TOOL_SWITCH, 'wakeWord', WakeButtonService.cancelIfWarmedUp)
    }

    // private static functions

    /**
     * Resolves deferred promise and hides the button
     */
    private static wakeButtonClicked () {
        WakeButtonService.resolveDeferredPromise()
    }

    /**
     * Publish an event, using EventBus, to enable the wake button
     */
    private static showButton () {
        console.debug('Enabling wakeButton')
        globalEventBus.publish(SeawardEventBusTopicsConstants.SET_WAKE_BUTTON_STATE, () => true)
    }

    /**
     * Publish an event, using EventBus, to disable the wake button
     */
    private static hideButton () {
        console.debug('Disabling wakeButton')
        globalEventBus.publish(SeawardEventBusTopicsConstants.SET_WAKE_BUTTON_STATE, () => false)
    }

    /**
     * Resolves deferred promise
     */
    private static resolveDeferredPromise () {
        if (!WakeButtonService.deferred) {
            console.debug('There is no promise to resolve')
            return
        }
        console.debug('Resolving wakeButton promise')
        WakeButtonService.deferred.resolve()
    }

    /**
     * Rejects deferred promise if any
     */
    private static rejectDeferredPromise () {
        if (!WakeButtonService.deferred) {
            console.debug('There is no promise to reject')
            return
        }
        console.debug('Cancelling wakeButton promise')
        WakeButtonService.deferred.reject(new WakeWordListeningInterruptedException({
            message: 'ListeningInterrupted',
        }))
    }

    /**
     * Cancels only if warmed up
     */
    private static cancelIfWarmedUp () {
        if (WakeButtonService.wakeButtonWarmedUp) {
            WakeButtonService.cancel()
        }
    }
}
